import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-all duration-200 ease-out disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98]',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary-hover hover:shadow-md disabled:bg-muted disabled:text-muted-foreground disabled:opacity-60 disabled:active:scale-100',
        outline:
          'border border-border bg-transparent text-foreground hover:bg-muted/50 hover:border-border-hover active:bg-muted disabled:text-muted-foreground disabled:opacity-60 disabled:active:scale-100',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-muted active:bg-muted/80 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-60 disabled:active:scale-100',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive-hover hover:shadow-md disabled:bg-muted disabled:text-muted-foreground disabled:opacity-60 disabled:active:scale-100',
        ghost:
          'text-foreground hover:bg-muted/60 active:bg-muted',
        link: 
          'text-primary underline-offset-4 hover:underline active:scale-100',
        glass:
          'bg-white/60 backdrop-blur-xl border border-white/20 text-foreground hover:bg-white/80 shadow-sm',
      },
      size: {
        default: 'h-10 px-5 py-2.5',
        sm: 'h-9 rounded-lg px-4 text-xs',
        lg: 'h-12 rounded-xl px-7 text-base',
        icon: 'size-10 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
